<template>
    <div
        class="trix-container min-w-0 bg-white dark:bg-dark-700 rounded-md"
        :class="{
            'bg-gray-200 dark:bg-dark-800':
                !addingPrivateNote && (mostrandoEmojiPicker || (anexos && anexos.length > 0) || recordingAudio),
            'bg-yellow-300 dark:bg-yellow-700 dark:text-gray-200': addingPrivateNote,
        }"
    >
        <div v-show="addingPrivateNote && !mostrandoEmojiPicker">
            <div class="p-2 flex items-center text-xs space-x-1">
                <alo-icon icon="outline-annotation " class="w-4 h-4 inline" />
                <span>{{ __('Anotações internas são visíveis somente por pessoas da sua equipe.') }}</span>
            </div>
        </div>

        <div
            v-if="quotedMessage"
            class="relative mx-6 m-2 p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-dark-800"
        >
            <div class="absolute top-0 right-0 -mt-2 -mr-2">
                <button class="p-1 rounded-full bg-gray-500 hover:bg-gray-700 text-white" @click="quotedMessage = null">
                    <alo-icon icon="outline-x" class="w-4 h-4" />
                </button>
            </div>

            <div class="flex items-center space-x-1">
                <alo-icon icon="outline-reply" class="w-4 h-4" />
                <div class="text-sm" v-html="__('Responder: <b>:name</b>', { name: quotedMessage.name })"></div>
            </div>

            <div class="max-h-[50px] max-w-prose overflow-hidden">
                <alo-purified-html class="font-light text-sm break-words" :html="quotedMessage.body" />
            </div>
        </div>

        <div v-show="mostrandoEmojiPicker">
            <remote-emoji-picker ref="emojiPicker" @pick="inserirEmoji($event)" />
        </div>

        <div class="flex items-end min-w-0 bg-white dark:bg-dark-700">
            <div class="flex items-center px-2 min-w-0" v-show="!recordingAudio">
                <div v-show="showClose && !mostrandoEmojiPicker">
                    <button
                        class="p-1 pb-5 text-gray-700 dark:text-gray-300 rounded-full hover:text-primary-700"
                        :title="__('Encerrar conversa')"
                        @click="$emit('close')"
                    >
                        <alo-icon icon="outline-x" class="w-5 h-5" />
                    </button>
                </div>

                <button
                    v-show="mostrandoEmojiPicker"
                    class="p-1 pb-5 text-gray-700 dark:text-gray-300 rounded-full hover:text-primary-700"
                    :title="__('Fechar emojis')"
                    @click="toggleEmojiPicker()"
                >
                    <alo-icon icon="outline-x" class="w-6 h-6" />
                </button>

                <button
                    v-show="addingPrivateNote && !mostrandoEmojiPicker"
                    class="p-1 pb-5 text-gray-700 dark:text-gray-300 rounded-full hover:text-primary-700"
                    :title="__('Cancelar anotação interna')"
                    @click="closePrivateNote()"
                >
                    <alo-icon icon="outline-x" class="w-6 h-6" />
                </button>

                <alo-dropdown posição="top-right" v-if="runningInApp && !addingPrivateNote">
                    <button
                        class="p-1 pb-5 text-gray-700 dark:text-gray-300 rounded-full hover:text-primary-700"
                        :title="__('Adicionar...')"
                    >
                        <alo-icon icon="solid-plus-circle" class="w-6 h-6" />
                    </button>

                    <template #menu>
                        <alo-dropdown-item @click.native="addingPrivateNote = true" close-dropdown>
                            {{ __('Anotação interna') }}
                        </alo-dropdown-item>
                    </template>
                </alo-dropdown>

                <button
                    class="p-1 pb-5 text-gray-700 dark:text-gray-300 rounded-full hover:text-primary-700"
                    :class="{ 'text-primary-500': mostrandoEmojiPicker }"
                    :title="__('Adicionar emoji')"
                    @click="toggleEmojiPicker()"
                >
                    <alo-icon icon="outline-emoji-happy" class="w-6 h-6" />
                </button>

                <label
                    v-show="canSendAttachment"
                    class="p-1 pb-5 rounded-full cursor-pointer hover:text-primary-700"
                    :class="{
                        'pointer-events-none text-gray-400 dark:text-gray-600': !podeAdicionarAnexo,
                        'text-gray-700 dark:text-gray-300': podeAdicionarAnexo,
                    }"
                    :title="__('Enviar arquivo')"
                >
                    <input ref="file" type="file" class="hidden" @change="upload" multiple />
                    <alo-icon
                        icon="outline-paper-clip"
                        class="w-6 h-6 text-gray-700 dark:text-gray-400 dark:hover:bg-dark-800"
                    />
                </label>
            </div>

            <div class="relative flex-1 mb-2 rich-text min-w-0" v-show="!recordingAudio">
                <div v-show="carregandoAnexoInline" class="my-1 text-gray-500">
                    {{ __('Carregando...') }}
                </div>
                <div v-show="anexos && anexos.length > 0" class="my-1">
                    <AnexosIndex :anexos="anexos" :mostrar-remover="true" @remover="removerAnexo($event)" />

                    <div
                        v-show="canal == 'whatsapp_qr_code' || canal == 'whatsapp_twilio' || canal == 'instagram'"
                        class="text-green-600 flex items-center"
                    >
                        <alo-icon icon="outline-check" class="w-5 h-5" />
                        <span>{{ __('Você pode enviar 1 anexo por mensagem.') }}</span>
                    </div>
                </div>

                <input :id="id" type="hidden" />

                <div
                    v-show="quickRepliesOptions"
                    class="
                        z-10
                        absolute
                        bottom-0
                        mb-12
                        left-0
                        w-full
                        rounded-lg
                        bg-white
                        dark:bg-dark-800 dark:text-gray-400
                        ring-1 ring-black ring-opacity-5
                        shadow-lg
                        flex
                    "
                    style="height: 300px"
                >
                    <div class="rounded-l-lg flex flex-col bg-white dark:bg-dark-900 p-2">
                        <a
                            v-show="canAddQuickReply"
                            class="
                                block
                                p-2
                                rounded
                                bg-white
                                dark:bg-dark-800 dark:hover:bg-dark-700
                                text-gray-400
                                hover:text-gray-900 hover:bg-gray-100
                                dark:hover:text-gray-200
                            "
                            :href="'/chat/quick_replies/create?id_space=' + workspaceId"
                            modal="lateral"
                            :próximo-changeset="
                                '[\'fechar_último_modal\', {dispatch: \'chat:quick_reply_created\', detail: {room_id: ' +
                                mensageiroId +
                                '}}]'
                            "
                        >
                            <alo-icon icon="outline-plus" class="w-6 h-6" />
                        </a>
                    </div>

                    <div class="relative bg-gray-50 dark:bg-dark-750 w-full overflow-auto">
                        <div
                            v-if="quickRepliesOptions && quickRepliesOptions.length == 0"
                            class="p-4 text-gray-500 flex justify-start"
                        >
                            {{ __('Nenhuma pré-programada encontrada.') }}
                        </div>

                        <div v-if="Array.isArray(quickRepliesOptions) || recentQuickReplies.records.length > 0">
                            <div v-if="recentQuickReplies.records.length > 0 && text.trim() == '/'">
                                <div class="px-4 pt-4 font-semibold uppercase text-sm text-secondary">
                                    {{ __('Usadas recentemente (:n)', { n: recentQuickReplies.records.length }) }}
                                </div>

                                <div class="p-2">
                                    <div
                                        v-for="quickReply in recentQuickReplies.records"
                                        :key="quickReply.id"
                                        @click="selectQuickReply(quickReply)"
                                        class="
                                            cursor-pointer
                                            text-sm
                                            p-2
                                            rounded-sm
                                            overflow-hidden
                                            hover:bg-primary-500 hover:text-white
                                            dark:hover:bg-primary-700 dark:hover:text-gray-200
                                            flex
                                            items-start
                                        "
                                    >
                                        <alo-icon icon="outline-reply" class="mt-0.5 mr-1 w-3.5 h-3.5 flex-shrink-0" />
                                        <div v-html="quickReply.mensagem"></div>
                                    </div>
                                </div>
                            </div>

                            <div class="px-4 pt-4 font-semibold uppercase text-sm text-secondary">
                                {{
                                    __('Respostas rápidas (:n)', {
                                        n: Array.isArray(quickRepliesOptions) ? quickRepliesOptions.length : '...',
                                    })
                                }}
                            </div>

                            <div
                                v-if="quickRepliesOptions === 'loading'"
                                class="p-4 text-gray-500 flex items-center justify-center space-x-2 p-6"
                            >
                                <alo-loading-ring />
                                <span>{{ __('Carregando...') }}</span>
                            </div>

                            <div class="p-2" v-if="Array.isArray(quickRepliesOptions)">
                                <div v-for="(quickReply, index) in quickRepliesOptions" :key="index">
                                    <div
                                        class="
                                            rich-text
                                            p-2
                                            text-sm
                                            hover:bg-primary-500 hover:text-white
                                            dark:hover:bg-primary-700 dark:hover:text-gray-200
                                            rounded-sm
                                            cursor-pointer
                                            flex
                                            items-start
                                        "
                                        @click="selectQuickReply(quickReply)"
                                    >
                                        <alo-icon icon="outline-reply" class="mt-0.5 mr-1 w-3.5 h-3.5 flex-shrink-0" />
                                        <div v-html="quickReply.mensagem"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <trix-editor
                    class="
                        border
                        py-2
                        pl-10
                        text-gray-900
                        shadow-sm
                        dark:focus:bg-dark-700 dark:text-gray-200
                        focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500
                        dark:hover:border-primary-500
                    "
                    id="trix-mensagem"
                    ref="editor"
                    @focus="talvezMarcarEventosComoLidos()"
                    :input="id"
                ></trix-editor>

                <div
                    v-show="text.trim().length == 0"
                    class="absolute inset-y-0 left-0 ml-[1.15rem] flex items-center z-10 opacity-50 pointer-events-none"
                >
                    {{ __('Digite sua mensagem...') }}
                </div>
            </div>

            <div class="w-full pt-3 pl-3 mb-4 dark:border-t dark:border-dark-600" v-if="recordingAudio">
                <div v-if="audioPermissionDenied" class="text-sm">
                    <div class="font-bold">{{ __('Não foi possível obter permissão para capturar o microfone.') }}</div>
                    {{ __(' Por favor, habilite essa permissão no navegador e recarregue essa página.') }}

                    <div>
                        <button @click="cancelarÁudio()" class="underline">
                            {{ __('Ok') }}
                        </button>
                    </div>
                </div>

                <div v-else>
                    <div class="flex items-center justify-center" v-show="!audioUploadInProgress">
                        <button
                            :title="__('Enviar')"
                            @click="enviarÁudio()"
                            class="bg-green-600 text-green-100 p-1 rounded-full"
                        >
                            <alo-icon icon="outline-check" class="w-6 h-6" />
                        </button>

                        <alo-cronômetro :relativo="áudioTimestamp" class="px-3" />

                        <button
                            :title="__('Cancelar')"
                            @click="cancelarÁudio()"
                            class="bg-red-600 text-green-100 p-1 rounded-full"
                        >
                            <alo-icon icon="outline-x" class="w-6 h-6" />
                        </button>
                    </div>

                    <div class="flex items-center justify-center" v-if="audioUploadInProgress">
                        <div class="text-sm">{{ __('Enviando...') }}</div>
                        <div class="w-44 h-2 bg-gray-400 rounded">
                            <div
                                class="h-2 bg-primary-600 rounded"
                                :style="'width:' + anexos[0].progressoUpload + '%'"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>

            <button
                class="flex-shrink-0 pl-4 pt-4 pr-4 pb-5"
                v-show="(!uploadEmProgresso && html.length > 0 && !recordingAudio) || !canSendAudio"
                @mousedown.prevent="enviarMensagem($event)"
                @click.prevent="enviarMensagem($event)"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    viewBox="0 0 512 512"
                >
                    <path
                        fill="currentColor"
                        d="M481.5 210.3L68.5 39C51 31.7 31.2 35 17 47.2A49.2 49.2 0 001.6 96.8L38.3 241h180a15 15 0 010 30h-180L1.6 415.2A49.2 49.2 0 0017 464.8a49.2 49.2 0 0051.3 8.3l413.1-171.4A48.7 48.7 0 00512 256a48.7 48.7 0 00-30.5-45.7z"
                    />
                </svg>
            </button>

            <button
                class="pl-2 pt-2 pr-4 pb-5"
                v-show="canSendAudio && !uploadEmProgresso && html.length == 0 && !recordingAudio"
                @click="gravarÁudio()"
            >
                <alo-icon
                    icon="outline-microphone"
                    class="
                        flex-shrink-0
                        w-8
                        h-8
                        text-gray-600
                        dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-500
                    "
                />
            </button>

            <button class="flex-shrink-0 mb-1 p-1" v-show="uploadEmProgresso">
                <alo-icon icon="outline-refresh" class="w-6 h-6 text-gray-600 dark:text-gray-200" />
            </button>
        </div>

        <div class="bg-white dark:bg-dark-700 rounded-md pl-32 -mt-1 pb-2 text-dark-100 h-6" v-if="runningInApp">
            <div v-if="runningInChat" class="flex gap-1">
                <div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        style="transform: rotateZ(-90deg) scaleX(-1)"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                        />
                    </svg>
                </div>
                <div style="font-size: 10px">
                    {{ __('Para mensagens rápidas e outros recursos digite "/" no início da mensagem') }}
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.trix-container trix-toolbar {
    display: none;
}

.trix-container trix-editor {
    width: 100%;
    padding: 8px 15px !important;
    border-radius: 5px !important;
    min-height: 40px !important;
    max-height: 300px;
    overflow: auto;
}
</style>

<script>
import axios from 'axios'
import { extrairJson } from '@App/Changeset'
import * as Str from '@Shared/String'
import * as Audio from '@Shared/AudioRecorder'
import * as CustomerTemplate from '@App/Utils/CustomerTemplate'
import { debounce, throttle } from '@Shared/Functions'
import { pad } from '@Shared/Date'
import { meta } from '@App/Meta'
import AnexosIndex from './Anexos/Index'
import '@App/Vendor/Trix'
import { canaisDoChat } from '@Shared/Chat/Store'
import { recentQuickReplies, add as addRecentQuickReply } from './RecentQuickReplies'
import { toast } from '@App/Toast'

/**
 * Esse componente é usado tanto na tela do operador quanto no widget do web messenger.
 * A prop `attrs` injeta as funcionalidades de acordo com cada ambiente.
 */
export default {
    components: { AnexosIndex },
    props: [
        'mensageiroType',
        'mensageiroId',
        'workspaceId',
        'canal',
        'attrs',
        'quickReplyVars',
        'canSendAudio',
        'canSendAttachment',
        'showClose',
    ],
    data() {
        let id = Math.random().toString(36).substring(7) + new Date().getTime().toString()

        return {
            addingPrivateNote: false,
            mostrandoEmojiPicker: false,
            selectionRange: null,
            carregandoAnexoInline: false,
            áudio: null,
            áudioTimestamp: null,
            audioPermissionDenied: false,
            quickRepliesOptions: null,
            quotedMessage: null,
            recentQuickReplies,
            anexos: [],
            id,
            html: '',
            text: '',
        }
    },
    watch: {
        html(novoValor, valorAntigo) {
            if (novoValor != valorAntigo) {
                this.digitandoThrottled()
            }

            this.saveDraftToLocalStorageDebounced()
        },
        anexos() {
            this.saveDraftToLocalStorageDebounced()
        },
        addingPrivateNote() {
            this.saveDraftToLocalStorageDebounced()
        },
    },
    async mounted() {
        if (!this.$refs.editor) return

        this.digitandoThrottled = throttle(
            () => {
                // Nós podemos não ter o ID da sala ainda no Widget antes do atendimento ser iniciado.
                // A primeira mensagem é o que faz abrir o atendimento.
                if (!this.mensageiroId) return

                this.attrs.fastlane('chat:digitando', {
                    broadcast: this.attrs.canaisDeBroadcast(this.mensageiroId),
                    mensageiro_type: this.mensageiroType,
                    mensageiro_id: this.mensageiroId,
                    autor: 'core_users:' + meta('user-id'),
                    autor_nome: meta('user-nome'),
                })
            },
            3000,
            true
        )

        this.saveDraftToLocalStorageDebounced = debounce(this.saveDraftToLocalStorage.bind(this), 300)
        this.searchForQuickRepliesDebounced = debounce(this.searchForQuickReplies.bind(this), 300)

        this.$refs.editor.addEventListener('trix-change', (ev) => {
            this.html = this.$refs.editor.value
            this.text = this.$refs.editor.editor.getDocument().toString()

            if (this.quickReplyVars && this.text.trim().indexOf('/') === 0 && this.text.length >= 2) {
                this.searchForQuickRepliesDebounced(this.text.replace('/', '').trim())
            } else if (this.quickRepliesOptions) {
                this.quickRepliesOptions = null
            }
        })

        this.$refs.editor.addEventListener('trix-selection-change', (ev) => {
            this.selectionRange = this.$refs.editor.editor.getSelectedRange()
        })

        this.$refs.editor.addEventListener('trix-attachment-add', async (ev) => {
            if (ev.attachment.file) {
                this.iniciarUploadAnexo(ev.attachment.file)
            }

            let isTrixAttachment =
                ev.attachment.attributes &&
                ev.attachment.attributes.values.content.indexOf('teclia-trix-attachment') === -1
            let isFile = ev.attachment.file

            if (isTrixAttachment || isFile) {
                ev.attachment.remove()
            }
        })

        this.$refs.editor.addEventListener('keydown', (ev) => {
            if (ev.key == 'Enter' && !ev.shiftKey) {
                this.enviarMensagem(ev)
            }
        })

        this.onQuotedMessage = (ev) => {
            if (ev.detail.room_id != this.mensageiroId) return
            this.quotedMessage = ev.detail

            this.$nextTick(() => {
                document.dispatchEvent(
                    new CustomEvent('chat:scroll-to-bottom', { detail: { id_sala: this.mensageiroId, force: true } })
                )
            })

            this.$el.querySelector('trix-editor').focus()
        }

        document.addEventListener('chat:quote-message', this.onQuotedMessage)

        this.onQuickReplySelectedFromPanel = (ev) => {
            if (ev.detail.room_id == this.mensageiroId) {
                this.selectQuickReply(ev.detail.quickReply)
            }
        }

        document.addEventListener('chat:quick-reply-selected-from-panel', this.onQuickReplySelectedFromPanel)

        this.restoreDraftFromLocalStorage()
    },
    destroyed() {
        document.removeEventListener('chat:quote-message', this.onQuotedMessage)
        document.removeEventListener('chat:quick-reply-selected-from-panel', this.onQuickReplySelectedFromPanel)
    },
    computed: {
        /**
         * Estamos rodando no app (e não no Widget)?
         * A palavra 'app' aqui não se refere a Apps mobile, mas sim a aplicação principal.
         */
        runningInApp() {
            return this.attrs.env == 'app'
        },

        /**
         * O usuário pode adicionar uma pré-programada?
         */
        canAddQuickReply() {
            return meta('policy-quick-replies') == 'true'
        },

        /**
         * Estamos rodando em uma sala de um canal do chat?
         * (Ao aposto do comunicador interno).
         */
        runningInChat() {
            return canaisDoChat.indexOf(this.canal) !== -1
        },

        uploadEmProgresso() {
            if (!this.anexos || this.anexos.length == 0) return false

            return this.anexos.find((anexo) => {
                return anexo.id_temporário
            })
        },

        /**
         * Nós estamos fazendo upload de um áudio que está sendo enviado?
         *
         * @return {boolean}
         */
        audioUploadInProgress() {
            if (this.anexos && this.anexos.length != 1) return false
            return this.anexos && this.anexos.length > 0 && this.anexos[0].nome_original == 'audio.ogg'
        },

        podeAdicionarAnexo() {
            if (this.canal == 'whatsapp_qr_code' || this.canal == 'whatsapp_twilio' || this.canal == 'instagram') {
                return this.anexos.length < 1
            }

            return true
        },

        recordingAudio() {
            return this.áudio !== null || this.audioUploadInProgress
        },
    },
    methods: {
        enviarMensagem(ev) {
            if (ev) ev.preventDefault()

            if (!this.text.trim() && this.anexos.length == 0) return

            let hasTemporaryAttachments = this.anexos && this.anexos.find((a) => a.id_temporário)
            if (hasTemporaryAttachments) return

            let htmlTrimmed = this.html
                .replace(/(<br>)+<\/div>$/, '</div>')
                .replace(/^<div>/, '')
                .replace(/<\/div>$/, '')

            let textTrimmed = this.text.trim()

            let extra = {}

            if (this.quotedMessage) {
                extra.quoted_message = this.quotedMessage
            }

            this.attrs.send({
                mensageiro_type: this.mensageiroType,
                mensageiro_id: this.mensageiroId,
                visibility: this.addingPrivateNote ? 'private' : 'public',
                corpo_html: htmlTrimmed,
                corpo_texto: textTrimmed,
                anexos: this.anexos,
                atributos_extras: extra,
            })

            this.limpar()
        },

        toggleEmojiPicker() {
            this.mostrandoEmojiPicker = !this.mostrandoEmojiPicker
        },

        inserirEmoji(emoji) {
            if (this.selectionRange) {
                this.$refs.editor.editor.setSelectedRange(this.selectionRange)
                this.$refs.editor.editor.insertString(emoji)
            } else {
                this.$refs.editor.editor.setSelectedRange([0, 0])
                this.$refs.editor.editor.insertString(emoji)
            }
        },

        closePrivateNote() {
            this.addingPrivateNote = false
        },

        gravarÁudio() {
            if (this.áudio) return
            this.áudio = Audio.record()
            this.áudioTimestamp = null
            this.áudio.onStart((timestamp) => {
                this.áudioTimestamp = timestamp
            })

            setTimeout(() => {
                if (this.áudio.permissionDenied()) {
                    this.audioPermissionDenied = true
                } else {
                    this.audioPermissionDenied = false
                }
            }, 500)
        },

        async searchForQuickReplies(term) {
            this.quickRepliesOptions = 'loading'

            let { data } = await axios.get(route('/chat/private_api/quick_replies'), {
                params: {
                    all: true,
                    room_id: this.mensageiroId,
                    query: term,
                },
            })

            let editor = this.$el.querySelector('trix-editor')
            if (editor.value.indexOf('/') != -1) {
                this.quickRepliesOptions = data
            } else {
                this.quickRepliesOptions = null
            }
        },

        enviarÁudio() {
            if (!this.áudio) return

            this.áudio.stop((file, duração_em_segundos) => {
                let minutos = Math.floor(duração_em_segundos / 60)
                let segundos = Math.ceil(duração_em_segundos - minutos * 60)
                let duração = pad(minutos) + ':' + pad(segundos)

                let uploadOpts = {
                    atributos_extras: { duração, duração_em_segundos: Math.ceil(duração_em_segundos), áudio: true },
                    autoSend: true,
                }

                if (this.canal == 'instagram' || this.canal == 'messenger') {
                    uploadOpts.converter_webm_para_mp4 = true
                } else {
                    uploadOpts.converter_webm_para_ogg = true
                }

                this.iniciarUploadAnexo(file, uploadOpts)

                this.áudio = null
            })
        },

        cancelarÁudio() {
            if (!this.áudio) return
            this.áudio.discard()
            this.áudio = null
        },

        upload() {
            for (let i = 0; i < this.$refs.file.files.length; i++) {
                let file = this.$refs.file.files[i]
                this.iniciarUploadAnexo(file)
            }

            this.$refs.file.value = ''
        },

        iniciarUploadAnexo(file, opts = {}) {
            let id_temporário = Str.random()

            let anexo = { id_temporário, progressoUpload: 0, nome_original: file.name }
            this.anexos.push(anexo)

            let data = new FormData()
            data.append('thumbnail', true)
            data.append('files[]', file)
            data.append('arquivável_type', this.mensageiroType)
            data.append('arquivável_id', this.mensageiroId)

            if (opts.atributos_extras) {
                data.append('atributos_extras', JSON.stringify(opts.atributos_extras))
            }

            if (opts.converter_webm_para_ogg) {
                data.append('converter_webm_para_ogg', 'true')
            }

            if (opts.converter_webm_para_mp4) {
                data.append('converter_webm_para_mp4', 'true')
            }

            anexo.cancelarUpload = axios.CancelToken.source()

            axios({
                method: 'post',
                url: '/api/core/files',
                data: data,
                timeout: 40000,
                cancelToken: anexo.cancelarUpload.token,
                onUploadProgress: (ev) => {
                    anexo.progressoUpload = Math.round((ev.loaded * 100) / ev.total)
                },
            })
                .then((res) => {
                    let arquivo = extrairJson(res.data, 'arquivos')[0]
                    let somethingWentWrong = !arquivo || (Array.isArray(arquivo) && arquivo.length == 0)

                    if (somethingWentWrong) {
                        this.anexos = this.anexos.filter((a) => a.id_temporário != id_temporário)
                        toast('error', __('Não foi possível realizar upload. Por favor, escolha outro arquivo.'))
                    } else {
                        this.anexos = this.anexos.map((anexo) => {
                            if (anexo.id_temporário == id_temporário) {
                                return arquivo
                            } else {
                                return anexo
                            }
                        })
                    }

                    if (opts.autoSend) {
                        this.$nextTick(() => {
                            this.enviarMensagem()
                        })
                    }
                })
                .catch((err) => {
                    // Se err.message for undefined, o upload foi cancelado pelo próprio usuário.
                    if (err.message === undefined) return

                    window.alert(__('Ops, não foi possível realizar upload. Tamanho máximo permitido: 30mb'))
                    this.anexos = this.anexos.filter((a) => a != anexo)
                })
        },

        removerAnexo(anexo) {
            if (anexo.cancelarUpload) {
                anexo.cancelarUpload.cancel()
            }

            this.anexos = this.anexos.filter((a) => a != anexo)
        },

        valorAnexos() {
            return this.anexos.filter((anexo) => anexo.id)
        },

        selectQuickReply(quickReply) {
            addRecentQuickReply(quickReply)

            let html = CustomerTemplate.render(quickReply.mensagem, this.quickReplyVars)

            if (this.$refs.editor) this.$refs.editor.value = html
            this.quickRepliesOptions = null

            this.$nextTick(() => {
                let trix = this.$el.querySelector('trix-editor')
                trix.focus()
                let editor = trix.editor
                let length = editor.getDocument().toString().length
                editor.setSelectedRange(length - 1)
            })
        },

        limpar() {
            this.html = ''
            this.text = ''
            this.anexos = []
            this.mostrandoEmojiPicker = false
            this.quotedMessage = null

            if (this.$refs.emojiPicker) this.$refs.emojiPicker.limpar()

            if (this.$refs.editor) this.$refs.editor.value = ''
        },

        talvezMarcarEventosComoLidos() {
            this.attrs.talvezMarcarEventosComoLidos()
        },

        /**
         * Salva a mensagem sendo digitada no localStorage para que o usuário possa voltar
         * a digitar mais tarde. Esse recurso é particulamente importante para emails porque
         * geralmente são respostas mais longas.
         */
        saveDraftToLocalStorage() {
            if (!this.text && !this.html && !this.anexos.length) {
                localStorage.removeItem(this.draftKey())
            }

            if (this.anexos && this.anexos.length > 0) {
                // Se estiver no meio de um upload, nós não devemos salvar no localStorage porque é
                // um estado inválido e temporário.
                let uploadInProgress = this.anexos.find((anexo) => {
                    return anexo.id_temporário
                })

                if (uploadInProgress) return
            }

            localStorage.setItem(
                this.draftKey(),
                JSON.stringify({
                    html: this.html,
                    text: this.text,
                    addingPrivateNote: this.addingPrivateNote,
                    anexos: this.anexos,
                })
            )
        },

        restoreDraftFromLocalStorage() {
            let value = localStorage.getItem(this.draftKey())

            if (value) {
                try {
                    let parsed = JSON.parse(value)

                    this.html = parsed.html
                    this.text = parsed.text

                    this.$refs.editor.value = parsed.html

                    this.addingPrivateNote = parsed.addingPrivateNote
                    this.anexos = parsed.anexos
                } catch (err) {
                    localStorage.removeItem(this.draftKey())
                }
            }
        },

        draftKey() {
            return 'chat_salas:draf:' + this.mensageiroId
        },
    },
}
</script>
